<template>
  <el-dialog id="project-crud"
             :title="((this.project) ? 'Edit' : 'Create') + ' Project'"
             @close="close"
             v-loading="loading"
             :element-loading-text="'Saving Project...'"
             :visible.sync="isVisible"
             :width="(isDialogFullscreen) ? '' : '480px'"
             :fullscreen="isDialogFullscreen">

    <div class="steps" v-if="showSteps">
      <el-steps :active="currentStep" finish-status="success" align-center>
        <el-step title="Details" icon=" "><icon slot="icon" name="edit" /></el-step>
        <el-step title="Estimates" icon=" "><icon slot="icon" name="regular/clock" /></el-step>
        <el-step v-if="!this.project" title="Group Task" icon=" "><icon slot="icon" name="users" /></el-step>
      </el-steps>
    </div>

    <!-- STEP 1 FORM -->
    <el-form :model="form"
             :rules="rules"
             ref="step-0-form"
             label-width="140px"
             class="user-form"
             v-show="currentStep === 0">
      <el-form-item label="Type" prop="projectType" v-if="!project || project.prospect">
        <el-radio-group v-model="form.projectType">
          <el-radio-button label="Prospect"></el-radio-button>
          <el-radio-button label="Approved"></el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Name" prop="name">
        <el-input v-model="form.name" auto-complete="off" />
      </el-form-item>
      <el-form-item label="Client" prop="client_id">
        <el-select v-model="form.client_id" filterable clearable placeholder="Type to filter">
          <el-option-group
            v-for="group in clientOptions"
            :key="group.label"
            :label="group.label">
            <el-option
              v-for="item in group.options"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-option-group>
        </el-select>
      </el-form-item>
      <el-form-item label="Account Manager" prop="account_manager_id">
        <el-select v-model="form.account_manager_id" filterable clearable placeholder="Type to filter">
          <el-option-group
            v-for="group in userOptions"
            :key="group.label"
            :label="group.label">
            <el-option
              v-for="item in group.options"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-option-group>
        </el-select>
      </el-form-item>
      <el-form-item label="Lead Designer" prop="lead_designer_id">
        <el-select v-model="form.lead_designer_id" filterable clearable placeholder="Type to filter">
          <el-option-group
            v-for="group in userOptions"
            :key="group.label"
            :label="group.label">
            <el-option
              v-for="item in group.options"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-option-group>
        </el-select>
      </el-form-item>
      <el-form-item label="Lead Developer" prop="lead_developer_id">
        <el-select v-model="form.lead_developer_id" filterable clearable placeholder="Type to filter">
          <el-option-group
            v-for="group in userOptions"
            :key="group.label"
            :label="group.label">
            <el-option
              v-for="item in group.options"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-option-group>
        </el-select>
      </el-form-item>
      <el-form-item label="Brief URL" prop="brief_url">
        <el-input v-model="form.brief_url" auto-complete="off" />
      </el-form-item>
      <el-form-item label="Proposal URL" prop="proposal_url">
        <el-input v-model="form.proposal_url" auto-complete="off" />
      </el-form-item>
      <el-form-item label="Active" prop="status" v-if="this.project && isAdmin">
        <el-switch v-model="form.status" />
      </el-form-item>
    </el-form>

    <!-- STEP 2 FORM -->
    <el-form :model="form"
             :rules="rules"
             ref="step-1-form"
             label-width="160px"
             class="user-form"
             v-show="currentStep === 1">
      <el-form-item label="Due Date" prop="dueDate">
        <el-date-picker
            v-model="form.dueDate"
            type="date"
            format="dd/MM/yyyy"
            placeholder="Pick a day"
            :picker-options="pickerOptions">
        </el-date-picker>
      </el-form-item>
      <el-form-item label="Estimated Hours" prop="est_hours">
        <el-input v-model="form.est_hours" auto-complete="off" type="number" min="0">
          <template slot="append">Hours</template>
        </el-input>
      </el-form-item>
      <el-form-item label="Hourly Rate" prop="hourly_rate" v-if="isAdmin">
        <el-input v-model="form.hourly_rate" auto-complete="off" type="number" min="0">
          <template slot="prepend">$</template>
        </el-input>
      </el-form-item>
      <el-form-item label="Project Components" prop="projectTags">
        <el-select
            v-model="form.projectTags"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="Choose or Add tags"
            @change="onEstimateTagChange"
        >
          <el-option
              v-for="item in tagOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value">
          </el-option>
        </el-select>
      </el-form-item>
      <div class="tag-estimates" v-if="form.tagEstimates.length">
        <el-form-item :label="estimate.tag"
                      v-for="(estimate, index) in form.tagEstimates"
                      :key="estimate.tag"
                      label-width="220px">
          <el-input v-model="form.tagEstimates[index].est_hours" auto-complete="off" type="number" min="0">
            <template slot="append">Hours</template>
          </el-input>
        </el-form-item>
      </div>
    </el-form>

    <!-- STEP 3 FORM -->
    <!-- New Project - Add Initial Group Task -->
    <el-form :model="form"
             :rules="rules"
             ref="step-2-form"
             label-width="140px"
             class="user-form"
             v-show="currentStep === 2">
      <el-form-item prop="createGroupTaskLabel" label-width="0" class="create-group-task">
        <el-radio-group v-model="createGroupTaskLabel">
          <el-radio-button label="Do Not Create Group Task"></el-radio-button>
          <el-radio-button label="Create Group Task"></el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item v-if="createGroupTask" label="Group Task Title" prop="group_task_title">
        <el-input v-model="form.group_task_title" auto-complete="off" placeholder="E.g. Prelim Work" />
      </el-form-item>
      <el-form-item v-if="createGroupTask" label="Group Task Members" prop="group_task_members">
        <user-selector v-model="form.group_task_members" />
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="goToLastStep">{{ this.currentStep === 0 ? 'Cancel' : 'Back' }}</el-button>
      <el-button type="primary" @click="gotoNextStep">{{ formHasMoreSteps ? 'Next' : 'Confirm'}}</el-button>
    </span>
  </el-dialog>
</template>

<script>
  import moment from 'moment'
  export default {
    name: 'project-crud',
    props: {
      project: null,
      clientId: null
    },
    data () {
      return {
        isVisible: true,
        loading: false,
        windowWidth: 0,

        createGroupTaskLabel: 'Do Not Create Group Task',

        pickerOptions: {
          disabledDate(time) {
            return time.getTime() < Date.now()
          },
        },

        showSteps: false, // We need to delay the render of the Steps, or Chrome gets it wrong
        currentStep: 0,

        form: {
          projectType: 'Prospect',
          name: '',
          client_id: null,
          account_manager_id: null,
          lead_designer_id: null,
          lead_developer_id: null,
          est_hours: 0,
          dueDate: null,
          hourly_rate: 220,
          brief_url: '',
          proposal_url: '',
          status: true,
          group_task_title: '',
          group_task_members: [],

          projectTags: [],
          tagEstimates: []
        },

        rules: {
          name: [
            { required: true, message: 'Name is required', trigger: 'blur' }
          ],
          client_id: [
            { required: true, message: 'Client is required', trigger: 'blur' }
          ],
          account_manager_id: [
            { required: true, message: 'Account Manager is required', trigger: 'blur' }
          ],
          lead_designer_id: [
            { required: true, message: 'Lead Designer is required', trigger: 'blur' }
          ],
          lead_developer_id: [
            { required: true, message: 'Lead Developer is required', trigger: 'blur' }
          ],
          group_task_title: [
            { required: true, message: 'Group Task Title is required', trigger: 'blur' }
          ],
          group_task_members: [
            { required: true, message: 'Group Task Members are required', trigger: 'blur' }
          ],
        }
      }
    },
    methods: {
      close () {
        this.$emit('close')
      },
      cancel () {
      },

      onEstimateTagChange (projectTags) {
        let tagEstimates = []
        // When project tags change we need to regenerate the list of Tag Estimate Fields
        for (let i = 0; i < projectTags.length; i++) {
          // The Tag select field will add existing tags as Integers (ids) and new tags as Strings
          let tag = {
            id: null,
            tag: '',
            est_hours: 0
          }

          // Collet the Tag details
          if (typeof projectTags[i] === 'string') {
            // New tag
            tag.tag = projectTags[i].trim()
          } else {
            // Existing tag
            tag.id = this.tags[projectTags[i]].id
            tag.tag = this.tags[projectTags[i]].tag
          }

          // Check if the user has already set a value for this tag
          for (let x = 0; x < this.form.tagEstimates.length; x++) {
            if (this.form.tagEstimates[x].tag.toLowerCase() === tag.tag.toLowerCase()) {
              tag.est_hours = this.form.tagEstimates[x].est_hours
            }
          }

          tagEstimates.push(tag)
        }

        // Final replace the tag estimates
        this.form.tagEstimates = tagEstimates.sort((a, b) => {
          return a.tag.toLowerCase() > b.tag.toLowerCase() ? 1 : -1
        })
      },

      goToLastStep () {
        if (this.currentStep === 0) {
          this.isVisible = false // Close the dialog
        } else {
          this.currentStep -= 1 // Just go back
        }
      },
      gotoNextStep () {
        let currentStep = this.currentStep
        let targetForm = `step-${currentStep}-form`

        this.$refs[targetForm].validate((valid) => {
          if (valid) {
            if (this.formHasMoreSteps) {
              this.currentStep += 1
            } else {
              this.submit()
            }
          }
        })
      },

      submit () {
        // @Note: See watcher 'saving' for the success/error handling of this action
        let dueDate = null
        if (this.form.dueDate != null) {
          dueDate = moment(this.form.dueDate).format('YYYY-MM-DD')
        }

        var object = {
          id: (this.project) ? this.project.id : null,
          name: this.form.name,
          client_id: this.form.client_id,
          account_manager_id: this.form.account_manager_id,
          lead_designer_id: this.form.lead_designer_id,
          lead_developer_id: this.form.lead_developer_id,
          brief_url: this.form.brief_url,
          proposal_url: this.form.proposal_url,
          est_hours: this.form.est_hours,
          hourly_rate: this.form.hourly_rate,
          due_date: dueDate,
          status: this.form.status,
          prospect: this.form.projectType === 'Prospect',
          tags: this.form.tagEstimates
        }
        if (this.project) {
          // A project was passed, update it
          this.$store.dispatch('updateProject', object)
        } else {
          // A project was not past, create a new one
          this.$store.dispatch('createProject', object)
        }
      },

      setProjectDetails () {
        if (this.project) {
          this.form.name = this.project.name
          this.form.client_id = this.project.client_id
          this.form.account_manager_id = this.project.account_manager_id
          this.form.lead_designer_id = this.project.lead_designer_id
          this.form.lead_developer_id = this.project.lead_developer_id
          this.form.brief_url = this.project.brief_url
          this.form.proposal_url = this.project.proposal_url
          this.form.est_hours = this.project.est_hours
          this.form.hourly_rate = this.project.hourly_rate
          this.form.dueDate = this.project.due_date !== null ? moment(this.project.due_date).toDate() : null
          this.form.status = this.project.status
          this.form.projectType = (!this.project.prospect) ? 'Approved' : 'Prospect'

          this.form.tagEstimates = !this.project.tags ? [] : this.project.tags.map(tag => {
            this.form.projectTags.push(tag.id)

            return {
              id: tag.id,
              tag: tag.tag,
              est_hours: tag.est_hours,
            }
          })
        } else if (this.clientId) {
          this.form.client_id = this.clientId
        }
      },
      getWindowWidth () {
        this.windowWidth = document.documentElement.clientWidth
      }
    },
    computed: {
      isDialogFullscreen () {
        return this.windowWidth < 520
      },
      // Does the form have more steps to complete?
      formHasMoreSteps () {
        if (this.project) {
          // The Group tasks step isn't available once the project has been created
          return this.currentStep < 1
        }
        return this.currentStep < 2
      },
      createGroupTask () {
        return this.createGroupTaskLabel === 'Create Group Task'
      },
      clientOptions () {
        // Options for the Clients select dropdown
        let clients = []
        let prospects = []

        Object.keys(this.clients)
          .map((k) => this.clients[k])
          .sort((a, b) => {
            return a.name > b.name ? 1 : -1
          })
          .map((client) => {
            let targetOptions = client.prospect ? prospects : clients
            targetOptions.push({
              label: client.name,
              value: client.id
            })
          })

        return [{
          label: 'Clients',
          options: clients
        },
        {
          label: 'Onboarding',
          options: prospects
        }]
      },

      userOptions () {
        // Group into Inactive and Active users
        let active = []
        let inactive = []

        Object.keys(this.users)
          .map((k) => this.users[k])
          .sort((a, b) => {
            return a.first_name > b.first_name ? 1 : -1
          })
          .map((user) => {
            let targetOptions = user.status === 1 ? active : inactive
            targetOptions.push({
              label: user.first_name + ' ' + user.last_name,
              value: user.id
            })
          })

        return [{
          label: 'Active',
          options: active
        },
        {
          label: 'Inactive',
          options: inactive
        }]
      },

      tagOptions () {
        return Object.keys(this.tags)
          .map((k) => this.tags[k])
          .sort((a, b) => {
            return a.tag > b.tag ? 1 : -1
          })
          .map((tag) => {
            return {
              label: tag.tag,
              value: tag.id
            }
          })
      },

      // Vuex Mappings
      clients () {
        return this.$store.state.clients.objects
      },
      users () {
        return this.$store.state.users.objects
      },
      tags () {
        return this.$store.state.tags.objects
      },
      saving () {
        return this.$store.state.projects.createInProgress || this.$store.state.projects.updateInProgress
      },
      savingGroupTask () {
        return this.$store.state.tasks.createInProgress
      },
      affectedProject () {
        if (this.project) {
          return this.$store.state.projects.updatedObject
        } else {
          return this.$store.state.projects.createdObject
        }
      },
      createdGroupTask () {
        return this.$store.state.tasks.createdObject
      },
      // Vuex Mappings
      isAdmin () {
        return this.$store.getters.isAdmin
      }
    },
    mounted () {
      this.setProjectDetails()
      // We need all Users and Clients for the dropdowns.. make sure we have everything
      this.$store.dispatch('getUsers')
      this.$store.dispatch('getClients')
      this.$store.dispatch('getTags')

      this.$nextTick(function () {
        window.addEventListener('resize', this.getWindowWidth)
        this.getWindowWidth()
        this.showSteps = true // We need to delay the render of the steps section, or the layout gets messed up by chrome
      })
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.getWindowWidth)
    },
    watch: {
      'project' () {
        this.setProjectDetails()
      },

      'saving' () {
        // If the Project store is either POSTing or PUTing, show the loader if the action is in progress
        // otherwise handle the expected result of a save
        if (this.saving) {
          this.loading = true
        } else {
          if (!this.affectedProject) {
            this.loading = false
            // There was an error creating/updaing the project.
            // @TODO: Implement error handling
            console.error('Create/Update Failed. @TODO: Add error handling')
          } else {
            this.$message({
              message: (this.project) ? 'Project updated!' : 'Project created!',
              type: 'success'
            })

            // If the user opt'd to create a group task we need to wait for that to complete as well..
            if (this.createGroupTask) {
              // We need to create a Group Task for the project..
              // Note that the result of this will be handled in the 'savingGroupTask' watcher below
              let task = {
                title: this.form.group_task_title,
                project_id: this.affectedProject.id,
                group_task: true,
                group_users: this.form.group_task_members,
                status: 0 // Task in Progress
              }
              this.$store.dispatch('createTask', task)
            } else {
              this.loading = false
              // No need to create a group task, go ahead and finish the process
              this.isVisible = false
              this.$emit('close', this.affectedProject)
            }
          }
        }
      },
      'savingGroupTask' () {
        // If the user opt'd to create a group task we need to wait for that to complete as well..
        if (this.savingGroupTask) {
          this.loading = true
        } else {
          this.loading = false
          if (!this.createdGroupTask) {
            console.error('Create/Update Failed. @TODO: Add error handling')
          } else {
            this.$message({
              message: 'Group Task created!',
              type: 'success'
            })
            this.isVisible = false
            this.$emit('close', this.affectedProject)
          }
        }
      }
    }
  }
</script>

<style lang="less" scoped>
  .el-select {
    width: 100%;
  }
  .steps {
    padding-bottom: 22px;
    margin-bottom: 22px;
    border-bottom: 1px solid #E4E4E4;
  }
  .tag-estimates {
    padding-top: 22px;
    border-top: 1px solid #E4E4E4;
  }
</style>
<style lang="less">
  #project-crud {
    .el-dialog__body {
      padding-top: 15px;
    }
    .steps {
      position: relative;
      .el-step__title {
        margin-top: 5px;
        font-size: 0.8rem;
        line-height: 1rem;
      }
    }
    .create-group-task {
      text-align: center;
    }
  }
</style>
